﻿import { Alert, Col, Form, Input, Modal, Row, Select } from 'antd';
import { ValidateErrorEntity } from 'rc-field-form/es/interface';
import EnumSelect from 'msg-ui-react/dist/components/data-entry/EnumSelect';

import React, { useEffect, useRef, useState } from 'react';
import { CapabilitiesResult, ExternalChartLayer, TileMatrixSet, WMSLayer, WMTSLayer } from './LayerInterfaces';
import { ExternalLayerTypes } from './LayerTypes';
import { getAutoSelectedMatrixSet, getTileUrlFromCapabilitiesUrl, readCapabilitiesWms, readCapabilitiesWmts } from './utils';


interface Props {
    layer: ExternalChartLayer | undefined;
    onOk: (layer: ExternalChartLayer) => void;
    onCancel: () => void;
}

const LayerModal: React.FC<Props> = (props) => {
    const InvalidUrl = "Invalid Url!";
    const [form] = Form.useForm<ExternalChartLayer>();

    const [invalidUrl, setInvalidUrl] = useState<boolean>()

    const [allMatrixSets, setAllMatrixSets] = useState<TileMatrixSet[]>([]);
    const [availableLayers, setAvailableLayers] = useState<WMSLayer[] | WMTSLayer[]>([]);
    const [availableMatrixSets, setAvailableMatrixSets] = useState<TileMatrixSet[]>([]);
    const [showUrl, setShowUrl] = useState<boolean>(false);

    const [selectedMatrixSet, setSelectedMatrixSet] = useState<string>("");

    const invalidUrlRef = useRef<boolean>();
    const capabilitiesPromise = useRef<Promise<void>>();
    const capabilitiesTimeout = useRef<number>();

    useEffect(() => {
        invalidUrlRef.current = invalidUrl;
    }, [invalidUrl])

    useEffect(() => {
        if (props.layer) {
            
            setShowUrl(props.layer.url !== "");

            applyCapabilitiesUrl(props.layer.capabilitiesUrl, props.layer.type);

            form.setFieldsValue({ ...props.layer });

            return () => {
                window.clearTimeout(capabilitiesTimeout.current);
                capabilitiesTimeout.current = undefined;
            }
        }
    }, [props.layer])

    useEffect(() => {
        if (props?.layer?.type === ExternalLayerTypes.WMTS)
            setAvailableMatrixSets(getMatrixSetsForSelectedLayer());
    }, [availableLayers])

    useEffect(() => {
        if (allMatrixSets.length > 0) {
            let autoSelectedMatrixSet = getAutoSelectedMatrixSet(availableMatrixSets);
            if (autoSelectedMatrixSet != "") {
                form.setFieldsValue({ matrixSet: autoSelectedMatrixSet });
                setSelectedMatrixSet(autoSelectedMatrixSet);
            }
            else
                console.log("Couldn't auto select a matrix set");
        }
    }, [availableMatrixSets])

    const onCancel = () => {
        props.onCancel();
    }

    const onFinish = (values: ExternalChartLayer) => {

        if (props.layer) {
            const layer = {
                ...props.layer,
                ...values                
            }

            props.onOk(layer);
        }
    }
    const getMatrixSetsForSelectedLayer = () => allMatrixSets?.filter(m => (availableLayers as WMTSLayer[]).find(l => l.TileMatrixSetLink.some(tmsl => tmsl.TileMatrixSet === m.Identifier))) ?? [];

    const applyCapabilitiesUrl = async (capabilitiesUrl: string, type: ExternalLayerTypes): Promise<CapabilitiesResult | undefined> => {
        
        setAvailableLayers([]);
        setAvailableMatrixSets([]);
        setSelectedMatrixSet("");

        if (capabilitiesUrl) {

            form.setFields([
                {
                    name: ['invalidUrl'],
                    errors: []
                }
            ])
            form.validateFields(['capabilitiesUrl']);

            try {
                let result: CapabilitiesResult;

                if (type === ExternalLayerTypes.WMS) {
                    result = await readCapabilitiesWms(capabilitiesUrl);
                    setAvailableLayers(result.layers);
                }
                else {
                    result = await readCapabilitiesWmts(capabilitiesUrl);
                    setAvailableLayers(result.layers);
                }

                return result;
            }
            catch (e) {

                form.setFields([
                    {
                        name: ['invalidUrl'],
                        errors: ['invalidUrl']
                    }
                ])

                form.validateFields(['capabilitiesUrl'])
            }
        }
        
    }

    const capabilitiesUrlChanged = async (capabilitiesUrl: string, type: ExternalLayerTypes) => {
        form.resetFields(["url", "layers", "matrixSet"]);
        setAvailableMatrixSets([]);
        setShowUrl(false);

        try {

            let capabilitiesResult = await applyCapabilitiesUrl(capabilitiesUrl, type);

            if (capabilitiesResult) {

                let tileUrl = getTileUrlFromCapabilitiesUrl(capabilitiesUrl, type);
                if (tileUrl !== "") {
                    form.setFieldsValue({ url: tileUrl });
                    setShowUrl(true);
                }

                if (type == ExternalLayerTypes.WMTS) {
                    if (capabilitiesResult.matrixSets === undefined)
                        throw new Error("No matrix set found in capabilities document");

                    setAllMatrixSets(capabilitiesResult.matrixSets);
                }
            }

        } catch (e) {
            form.resetFields(["url", "layers", "matrixSet"]);
            setShowUrl(false);
            setAvailableLayers([]);
            setAllMatrixSets([]);
            setAvailableMatrixSets([]);
        }
    }

    const renderLayerSelect = (type: ExternalLayerTypes) => {
        if (!availableLayers.length)
            return;

        let options: JSX.Element[] = [];
        if (type === ExternalLayerTypes.WMS) 
            return (
                <Select
                    mode="multiple"
                    allowClear
                >
                    {(availableLayers as WMSLayer[]).map(l => <Select.Option key={l.Name} value={l.Name}>{l.Title}</Select.Option>)}
                </Select>
            );        
        else
            return (
                <Select
                    allowClear
                >
                    {(availableLayers as WMTSLayer[]).map(l => <Select.Option key={l.Identifier} value={l.Identifier}>{l.Title}</Select.Option>)}
                </Select>
            );        
    }

    const isValid = (key: keyof ExternalChartLayer, entity: ValidateErrorEntity<ExternalChartLayer> | undefined) => {
        if (!entity || !entity.errorFields.length)
            return true;
        return !entity.errorFields.some(f => f.name.includes(key) && f.errors.filter(e => e !== InvalidUrl).length);
    }

    const getDisplayCrs = (crs: string) => {
        let crsString = "crs:";

        let index = crs.indexOf(crsString);
        if (index < 1)
            return crs;
        else
            return crs.substring(index + crsString.length);
    }

    return (
        <>
            {
                props.layer &&
                <Modal
                    open={true}
                    width="60%"
                    title={props.layer?.id === -1 ? "Create a new collection" : "Update a collection"}
                    okText={props.layer?.id === -1 ? "Create" : "Update"}
                    cancelText="Cancel"                    
                    onCancel={onCancel}                    
                    onOk={() => {
                        form.submit();
                    }}
                >
                    <Form form={form}
                        onFinish={onFinish}
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 14 }}
                        
                        onValuesChange={async (changes, all) => {
                            const updates = changes as Partial<ExternalChartLayer>;

                            var errorEntity: ValidateErrorEntity<ExternalChartLayer> | undefined = undefined
                            try {
                                await form.validateFields();
                            } catch (e) {
                                errorEntity = e as ValidateErrorEntity<ExternalChartLayer>;
                            }

                            const urlValid = isValid('capabilitiesUrl', errorEntity);
                            const typeValid = isValid('type', errorEntity);

                            if (updates.capabilitiesUrl !== undefined && urlValid && typeValid) {
                                const url = updates.capabilitiesUrl;
                                window.clearTimeout(capabilitiesTimeout.current);
                                capabilitiesTimeout.current = window.setTimeout(async () => {

                                    if (capabilitiesPromise.current)
                                        await capabilitiesPromise.current;

                                    capabilitiesPromise.current = capabilitiesUrlChanged(url, all.type)
                                    await capabilitiesPromise.current;
                                    capabilitiesPromise.current = undefined;
                                }, 500);
                            }

                            if (updates.type !== undefined && typeValid) {
                                form.resetFields(["capabilitiesUrl", "url", "layers", "matrixSet"]);
                                setShowUrl(false);
                                setAvailableLayers([]);
                                setAllMatrixSets([]);
                                setAvailableMatrixSets([]);
                            }

                            if (updates.layers !== undefined && typeValid) {
                                if (all.type === ExternalLayerTypes.WMTS) {
                                    setAvailableMatrixSets(getMatrixSetsForSelectedLayer());
                                }
                            }

                            if (updates.matrixSet !== undefined) {
                                setSelectedMatrixSet(updates.matrixSet);
                            }                            
                        }}
                    >
                        <Form.Item
                            label="Layer type"
                            name="type"
                        >
                            <EnumSelect enumType={ExternalLayerTypes} width={70} />
                        </Form.Item>

                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[
                                { required: true, message: 'Please input name!' }
                            ]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            label="Capabilities Url"
                            name="capabilitiesUrl"
                            rules={[
                                { required: true, message: 'Please input url!' },
                                { type: "url", message: "This field must be a valid url." },
                                ({ getFieldValue, getFieldError }) => ({
                                    validator(obj, value) {

                                        const e = getFieldError('invalidUrl');

                                        if (e.includes("invalidUrl"))
                                            return Promise.reject(new Error(InvalidUrl));
                                        else
                                            return Promise.resolve();
                                    }                                    
                                }),
                            ]}                            
                        >
                            <Input />
                        </Form.Item>

                        {
                            showUrl &&
                            <Form.Item
                                label="Url"
                                name="url"
                                rules={[
                                    { required: true, message: 'Please input url!' },
                                    { type: "url", message: "This field must be a valid url." }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        }

                        {
                            availableLayers.length >= 1 &&
                            <Form.Item
                                label="Layer"
                                name="layers"
                                rules={[{ required: true, message: 'Please input layer!' }]}
                            >
                                {renderLayerSelect(form.getFieldValue("type"))}
                            </Form.Item>
                        }

                        {
                            availableMatrixSets.length > 0 &&
                            <Form.Item
                                label="Matrix Set"
                                name="matrixSet"
                                rules={[{ required: true, message: 'Please input Matrix set!' }]}
                            >
                                <Select>
                                    {
                                        availableMatrixSets.map(m => <Select.Option key={m.Identifier} value={m.Identifier}>{m.Identifier} ({getDisplayCrs(m.SupportedCRS)})</Select.Option>)
                                    }
                                </Select>
                            </Form.Item>
                        }

                        {
                            availableLayers.length >= 1 && availableMatrixSets.length > 0 && selectedMatrixSet === "" &&
                            <Row justify="start">
                               <Col span={4} />
                                <Col span={14}>
                                    <Alert message="Unable to auto select matrix set, could not select a matrix set which matches the map projection" type="warning" showIcon closable />
                                </Col>
                            </Row>
                        }

                        <Form.Item
                            name='invalidUrl'
                            hidden={true}
                        >
                            <Input />
                        </Form.Item>


                    </Form>
                </Modal>
            }
        </>
    );
}

export default LayerModal;